import * as yup from 'yup';

function requiredIfPR<T extends yup.AnySchema>(
  schemaType: T,
  message: string,
): T {
  return schemaType.when(['citizenship '], {
    is: (citizenship: string) =>
      citizenship === 'australian_pr_visa' ||
      citizenship === 'australian_tr_visa',
    then: (schema: T) => schema.required(message),
    otherwise: (schema: T) => schema.notRequired(),
  });
}

const MAX_PROPERTIES = 6;

function requiredIfIncomeSource<T extends yup.AnySchema>(
  schemaType: T,
  incomeSources: string[],
  message: string,
  disableRequired = true,
): T {
  if (disableRequired) return schemaType.notRequired();

  return schemaType.when('incomeSource', {
    is: (incomeSource: string) => incomeSources.includes(incomeSource),
    then: (schema: T) => schema.required(message),
    otherwise: (schema: T) => schema.notRequired(),
  });
}

function requiredIfCondition<T extends yup.AnySchema>(
  schemaType: T,
  fields: string[],
  condition: (...values: any[]) => boolean,
  message: string,
  disableRequired = true,
): T {
  if (disableRequired) return schemaType.notRequired();

  return schemaType.when(fields, {
    is: (...fieldValues: any[]) => condition(...fieldValues),
    then: (schema: T) => schema.required(message),
    otherwise: (schema: T) => schema.notRequired(),
  });
}

function requiredIfCoapplicantIncomeSource<T extends yup.AnySchema>(
  schemaType: T,
  incomeSources: string[],
  message: string,
  disableRequired = true,
): T {
  if (disableRequired) return schemaType.notRequired();

  return schemaType.when('coapplicantIncomeSource', {
    is: (incomeSource: string) => incomeSources.includes(incomeSource),
    then: (schema: T) => schema.required(message),
    otherwise: (schema: T) => schema.notRequired(),
  });
}

type NonPropertyFields = {
  [key: string]: yup.Schema<any>;
};

const nonPropertyFields: NonPropertyFields = {};

for (let i = 1; i <= MAX_PROPERTIES; i++) {
  const condition = (nonPropertyLoan: any) =>
    typeof nonPropertyLoan === 'number' && nonPropertyLoan >= i;

  // Loan Type
  nonPropertyFields[`nonPropertyLoanType${i}`] = requiredIfCondition(
    yup.string(),
    ['nonPropertyLoan'],
    condition,
    `Please select non property loan type ${i}`,
  );

  // Loan Currency
  nonPropertyFields[`nonPropertyLoanCurrency${i}`] = requiredIfCondition(
    yup.string(),
    ['nonPropertyLoan'],
    condition,
    `Please select non property loan currency ${i}`,
  );

  // Loan Balance
  nonPropertyFields[`nonPropertyLoanBalance${i}`] = requiredIfCondition(
    yup.number().typeError(`Non Property ${i} loan balance must be a number`),
    ['nonPropertyLoan'],
    condition,
    `Please enter non property loan balance ${i}`,
  );

  // Repayment Term
  nonPropertyFields[`nonPropertyLoanRepaymentTerm${i}`] = requiredIfCondition(
    yup.number().typeError(`Non Property ${i} loan repayment must be a number`),
    ['nonPropertyLoan'],
    condition,
    `Please enter non property repayment loan term ${i}`,
  );

  // Repayment Sum
  nonPropertyFields[`nonPropertyLoanRepaymentSum${i}`] = requiredIfCondition(
    yup
      .number()
      .typeError(`Non Property ${i} loan repayment sum must be a number`),
    ['nonPropertyLoan'],
    condition,
    `Please enter non property repayment loan sum ${i}`,
  );

  // Repayment Frequency
  nonPropertyFields[`nonPropertyLoanRepaymentFrequency${i}`] =
    requiredIfCondition(
      yup.string(),
      ['nonPropertyLoan'],
      condition,
      `Please select non property repayment frequency ${i}`,
    );
}

type CoapplicantNonPropertyFields = {
  [key: string]: yup.Schema<any>;
};

const coapplicantNonPropertyFields: CoapplicantNonPropertyFields = {};

for (let i = 1; i <= MAX_PROPERTIES; i++) {
  const condition = (nonPropertyLoan: any) =>
    typeof nonPropertyLoan === 'number' && nonPropertyLoan >= i;

  const coapplicantCondition = (coapplicantNonPropertyLoan: any) =>
    typeof coapplicantNonPropertyLoan === 'number' &&
    coapplicantNonPropertyLoan >= i;

  // Applicant Non-Property Fields
  nonPropertyFields[`nonPropertyLoanType${i}`] = requiredIfCondition(
    yup.string(),
    ['nonPropertyLoan'],
    condition,
    `Please select non property loan type ${i}`,
  );

  nonPropertyFields[`nonPropertyLoanCurrency${i}`] = requiredIfCondition(
    yup.string(),
    ['nonPropertyLoan'],
    condition,
    `Please select non property loan currency ${i}`,
  );

  nonPropertyFields[`nonPropertyLoanBalance${i}`] = requiredIfCondition(
    yup.number().typeError(`Non Property ${i} loan balance must be a number`),
    ['nonPropertyLoan'],
    condition,
    `Please enter non property loan balance ${i}`,
  );

  nonPropertyFields[`nonPropertyLoanRepaymentTerm${i}`] = requiredIfCondition(
    yup.number().typeError(`Non Property ${i} loan repayment must be a number`),
    ['nonPropertyLoan'],
    condition,
    `Please enter non property repayment loan term ${i}`,
  );

  nonPropertyFields[`nonPropertyLoanRepaymentSum${i}`] = requiredIfCondition(
    yup
      .number()
      .typeError(`Non Property ${i} loan repayment sum must be a number`),
    ['nonPropertyLoan'],
    condition,
    `Please enter non property repayment loan sum ${i}`,
  );

  nonPropertyFields[`nonPropertyLoanRepaymentFrequency${i}`] =
    requiredIfCondition(
      yup.string(),
      ['nonPropertyLoan'],
      condition,
      `Please select non property repayment frequency ${i}`,
    );

  // Co-Applicant Non-Property Fields
  coapplicantNonPropertyFields[`coapplicantNonPropertyLoanType${i}`] =
    requiredIfCondition(
      yup.string(),
      ['coapplicantNonPropertyLoan'],
      coapplicantCondition,
      `Please select co-applicant non-property loan type ${i}`,
    );

  coapplicantNonPropertyFields[`coapplicantNonPropertyLoanCurrency${i}`] =
    requiredIfCondition(
      yup.string(),
      ['coapplicantNonPropertyLoan'],
      coapplicantCondition,
      `Please select co-applicant non-property loan currency ${i}`,
    );

  coapplicantNonPropertyFields[`coapplicantNonPropertyLoanBalance${i}`] =
    requiredIfCondition(
      yup
        .number()
        .typeError(
          `Co-applicant Non-Property ${i} loan balance must be a number`,
        ),
      ['coapplicantNonPropertyLoan'],
      coapplicantCondition,
      `Please enter co-applicant non-property loan balance ${i}`,
    );

  coapplicantNonPropertyFields[`coapplicantNonPropertyLoanRepaymentTerm${i}`] =
    requiredIfCondition(
      yup
        .number()
        .typeError(
          `Co-applicant Non-Property ${i} loan repayment term must be a number`,
        ),
      ['coapplicantNonPropertyLoan'],
      coapplicantCondition,
      `Please enter co-applicant non-property loan repayment term ${i}`,
    );

  coapplicantNonPropertyFields[`coapplicantNonPropertyLoanRepaymentSum${i}`] =
    requiredIfCondition(
      yup
        .number()
        .typeError(
          `Co-applicant Non-Property ${i} loan repayment sum must be a number`,
        ),
      ['coapplicantNonPropertyLoan'],
      coapplicantCondition,
      `Please enter co-applicant non-property loan repayment sum ${i}`,
    );

  coapplicantNonPropertyFields[
    `coapplicantNonPropertyLoanRepaymentFrequency${i}`
  ] = requiredIfCondition(
    yup.string(),
    ['coapplicantNonPropertyLoan'],
    coapplicantCondition,
    `Please select co-applicant non-property loan repayment frequency ${i}`,
  );
}

export const incomeDetailSchema = yup.object().shape({
  incomeSource: yup.string().required('Please select income source option.'),
  nonPropertyLoan: yup
    .number()
    .required('please verify your non property loan'),
  whereDidYouHearAboutUs: yup
    .string()
    .required('Please select where did you hear about us option.'),
  creditCardCurrency: yup
    .string()
    .required('Please verify your credit card currency'),
  creditCardLimit: yup.number().required('Credit card limit is required'),
  userMessage: yup.string(),
  taxBenefitAwareness: yup.string().required('Please select any option.'),

  jobStartMonth: requiredIfIncomeSource(
    yup.string(),
    ['employee'],
    'Please select job start month.',
  ),
  jobStartYear: requiredIfIncomeSource(
    yup.string(),
    ['employee'],
    'Please select job start year.',
  ),
  occupation: requiredIfIncomeSource(
    yup.string(),
    ['employee'],
    'Occupation is required.',
  ),
  salaryCurrency: requiredIfIncomeSource(
    yup.string(),
    ['employee'],
    'Please select your salary currency.',
  ),
  companySalaryCurrency: requiredIfIncomeSource(
    yup.string(),
    ['self_employed_company', 'self_employed_trader_or_contractor'],
    'Please select your company salary currency.',
  ),
  baseSalary: requiredIfIncomeSource(
    yup.number(),
    ['employee'],
    'Please enter your base salary.',
  ),
  salaryFrequency: requiredIfIncomeSource(
    yup.string(),
    ['employee'],
    'Please select your salary frequency.',
  ),
  receiveAllowanceIncome: requiredIfIncomeSource(
    yup.boolean(),
    ['employee'],
    'Please verify your allowance income.',
  ),
  receiveCommissionIncome: requiredIfIncomeSource(
    yup.boolean(),
    ['employee'],
    'Please verify your commission income.',
  ),
  receiveAnnualCashBonus: requiredIfIncomeSource(
    yup.boolean(),
    ['employee'],
    'Please verify your annual cash bonus.',
  ),
  receiveStockUnit: requiredIfIncomeSource(
    yup.boolean(),
    ['employee'],
    'Please verify your stock units.',
  ),

  industry: requiredIfIncomeSource(
    yup.string(),
    ['self_employed_company', 'self_employed_trader_or_contractor'],
    'Please select your industry.',
  ),
  operationStartMonth: requiredIfIncomeSource(
    yup.string(),
    ['self_employed_company', 'self_employed_trader_or_contractor'],
    'Please select operation start month.',
  ),
  operationStartYear: requiredIfIncomeSource(
    yup.string(),
    ['self_employed_company', 'self_employed_trader_or_contractor'],
    'Please select operation start year.',
  ),
  thisYearPersonalNetIncomeAfterTaxes: requiredIfIncomeSource(
    yup.number(),
    ['self_employed_company', 'self_employed_trader_or_contractor'],
    'Please enter your personal net income after taxes.',
  ),
  lastYearPersonalNetIncomeAfterTaxes: requiredIfIncomeSource(
    yup.number(),
    ['self_employed_company', 'self_employed_trader_or_contractor'],
    'Please enter your personal net income after taxes.',
  ),

  ownershipPercentage: requiredIfIncomeSource(
    yup.number(),
    ['self_employed_company'],
    'Please enter ownership percentage.',
  ),
  thisYearCompanyNetIncomeAfterTaxes: requiredIfIncomeSource(
    yup.number(),
    ['self_employed_company'],
    'Please enter your company net income after taxes.',
  ),
  lastYearCompanyNetIncomeAfterTaxes: requiredIfIncomeSource(
    yup.number(),
    ['self_employed_company'],
    'Please enter your company net income after taxes.',
  ),
  allowanceIncome: requiredIfCondition(
    yup.number().typeError('Allowance income must be a number'),
    ['incomeSource', 'receiveAllowanceIncome'],
    (incomeSource, receiveAllowanceIncome) =>
      incomeSource === 'employee' && receiveAllowanceIncome === true,
    'Please enter your allowance income.',
  ),
  allowanceFrequency: requiredIfCondition(
    yup.string(),
    ['incomeSource', 'receiveAllowanceIncome'],
    (incomeSource, receiveAllowanceIncome) =>
      incomeSource === 'employee' && receiveAllowanceIncome === true,
    'Please select your allowance frequency.',
  ),
  typeOfAllowanceIncome: requiredIfCondition(
    yup.array(),
    ['incomeSource', 'receiveAllowanceIncome'],
    (incomeSource, receiveAllowanceIncome) =>
      incomeSource === 'self_employed_company' &&
      receiveAllowanceIncome === true,
    'Please enter the allowance income type.',
  ),
  commissionIncome: yup.number().when('receiveCommissionIncome', {
    is: true,
    then: (schema) => schema.required('Please enter your commission income'),
    otherwise: (schema) => schema.notRequired(),
  }),
  commissionFrequency: requiredIfCondition(
    yup.string(),
    ['incomeSource', 'receiveCommissionIncome'],
    (incomeSource, receiveCommissionIncome) =>
      incomeSource === 'self_employed_company' &&
      receiveCommissionIncome === true,
    'Please select your commission frequency',
  ),
  commissionYears: requiredIfCondition(
    yup.string(),
    ['incomeSource', 'receiveCommissionIncome'],
    (incomeSource, receiveCommissionIncome) =>
      incomeSource === 'self_employed_company' &&
      receiveCommissionIncome === true,
    'Please enter your commission years',
  ),
  lastYearAnnualCashBonus: requiredIfCondition(
    yup.number(),
    ['incomeSource', 'receiveAnnualCashBonus'],
    (incomeSource, receiveAnnualCashBonus) =>
      incomeSource === 'self_employed_company' &&
      receiveAnnualCashBonus === true,
    'Please enter your annual cash bonus',
  ),
  thisYearAnnualCashBonus: requiredIfCondition(
    yup.number(),
    ['incomeSource', 'receiveAnnualCashBonus'],
    (incomeSource, receiveAnnualCashBonus) =>
      incomeSource === 'self_employed_company' &&
      receiveAnnualCashBonus === true,
    'Please enter your annual cash bonus',
  ),
  lastYearStockOptions: requiredIfCondition(
    yup.number(),
    ['incomeSource', 'receiveStockUnit'],
    (incomeSource, receiveStockUnit) =>
      incomeSource === 'self_employed_company' && receiveStockUnit === true,
    'Please enter your stock units',
  ),
  thisYearStockOptions: requiredIfCondition(
    yup.number(),
    ['incomeSource', 'receiveStockUnit'],
    (incomeSource, receiveStockUnit) =>
      incomeSource === 'self_employed_company' && receiveStockUnit === true,
    'Please enter your stock units',
  ),

  // Coapplicant Schema
  coapplicantExists: yup.boolean().required('Please select any option.'),
  coapplicantNonPropertyLoan: requiredIfCondition(
    yup.number(),
    ['coapplicantExists'],
    (coapplicantExists) => coapplicantExists === true,
    'Please select any option.',
  ),
  coapplicantIncomeSource: requiredIfCondition(
    yup.string(),
    ['coapplicantExists'],
    (coapplicantExists) => coapplicantExists === true,
    'Please enter coaaplicant income source',
  ),
  coapplicantCreditCardCurrency: requiredIfCondition(
    yup.string(),
    ['coapplicantExists'],
    (coapplicantExists) => coapplicantExists === true,
    'Please enter coaaplicant credit card currency',
  ),
  coapplicantCreditCardLimit: requiredIfCondition(
    yup.number(),
    ['coapplicantExists'],
    (coapplicantExists) => coapplicantExists === true,
    'Please enter coaaplicant credit card limit',
  ),

  coapplicantJobStartMonth: requiredIfCoapplicantIncomeSource(
    yup.string(),
    ['employee'],
    'Please select job start month.',
  ),
  coapplicantJobStartYear: requiredIfCoapplicantIncomeSource(
    yup.string(),
    ['employee'],
    'Please select job start year.',
  ),
  coapplicantOccupation: requiredIfCoapplicantIncomeSource(
    yup.string(),
    ['employee'],
    'Occupation is required.',
  ),
  coapplicantSalaryCurrency: requiredIfCoapplicantIncomeSource(
    yup.string(),
    ['employee'],
    'Please select your salary currency.',
  ),
  coapplicantCompanySalaryCurrency: requiredIfCoapplicantIncomeSource(
    yup.string(),
    ['self_employed_company', 'self_employed_trader_or_contractor'],
    'Please select your company salary currency.',
  ),
  coapplicantBaseSalary: requiredIfCoapplicantIncomeSource(
    yup.number(),
    ['employee'],
    'Please enter your base salary.',
  ),
  coapplicantSalaryFrequency: requiredIfCoapplicantIncomeSource(
    yup.string(),
    ['employee'],
    'Please select your salary frequency.',
  ),
  coapplicantReceiveAllowanceIncome: requiredIfCoapplicantIncomeSource(
    yup.boolean(),
    ['employee'],
    'Please verify your allowance income.',
  ),
  coapplicantAllowanceIncome: requiredIfCondition(
    yup.number().typeError('Allowance income must be a number'),
    ['coapplicantIncomeSource', 'coapplicantReceiveAllowanceIncome'],
    (incomeSource, receiveAllowanceIncome) =>
      incomeSource === 'employee' && receiveAllowanceIncome === true,
    'Please enter your allowance income.',
  ),
  coapplicantAllowanceFrequency: requiredIfCondition(
    yup.string(),
    ['coapplicantIncomeSource', 'coapplicantReceiveAllowanceIncome'],
    (incomeSource, receiveAllowanceIncome) =>
      incomeSource === 'employee' && receiveAllowanceIncome === true,
    'Please select your allowance frequency.',
  ),
  coapplicantTypeOfAllowanceIncome: requiredIfCondition(
    yup.array().of(yup.string()),
    ['coapplicantIncomeSource', 'coapplicantReceiveAllowanceIncome'],
    (incomeSource, receiveAllowanceIncome) =>
      incomeSource === 'employee' && receiveAllowanceIncome === true,
    'Please select your allowance type.',
  ),
  coapplicantReceiveCommissionIncome: requiredIfCoapplicantIncomeSource(
    yup.boolean(),
    ['employee'],
    'Please verify your commission income.',
  ),
  coapplicantCommissionFrequency: requiredIfCondition(
    yup.string(),
    ['coapplicantIncomeSource', 'coapplicantReceiveCommissionIncome'],
    (incomeSource, receiveCommissionIncome) =>
      incomeSource === 'employee' && receiveCommissionIncome === true,
    'Please select your commission frequency.',
  ),
  coapplicantCommissionYears: requiredIfCondition(
    yup.string(),
    ['coapplicantIncomeSource', 'coapplicantReceiveCommissionIncome'],
    (incomeSource, receiveCommissionIncome) =>
      incomeSource === 'employee' && receiveCommissionIncome === true,
    'Please enter your commission years.',
  ),
  coapplicantCommissionIncome: requiredIfCondition(
    yup.number(),
    ['coapplicantIncomeSource', 'coapplicantReceiveCommissionIncome'],
    (incomeSource, receiveCommissionIncome) =>
      incomeSource === 'employee' && receiveCommissionIncome === true,
    'Please enter your commission income.',
  ),
  coapplicantReceiveAnnualCashBonus: requiredIfCoapplicantIncomeSource(
    yup.boolean(),
    ['employee'],
    'Please verify your annual cash bonus.',
  ),
  coapplicantThisYearAnnualCashBonus: requiredIfCondition(
    yup.number(),
    ['coapplicantIncomeSource', 'coapplicantReceiveAnnualCashBonus'],
    (incomeSource, receiveAnnualCashBonus) =>
      incomeSource === 'employee' && receiveAnnualCashBonus === true,
    'Please enter your annual cash bonus.',
  ),
  coapplicantLastYearAnnualCashBonus: requiredIfCondition(
    yup.number(),
    ['coapplicantIncomeSource', 'coapplicantReceiveAnnualCashBonus'],
    (incomeSource, receiveAnnualCashBonus) =>
      incomeSource === 'employee' && receiveAnnualCashBonus === true,
    'Please enter your annual cash bonus.',
  ),
  coapplicantReceiveStockUnit: requiredIfCoapplicantIncomeSource(
    yup.boolean(),
    ['employee'],
    'Please verify your stock units.',
  ),
  coapplicantThisYearStockOptions: requiredIfCondition(
    yup.number(),
    ['coapplicantIncomeSource', 'coapplicantReceiveStockUnit'],
    (incomeSource, receiveStockUnit) =>
      incomeSource === 'employee' && receiveStockUnit === true,
    'Please enter your stock units.',
  ),
  coapplicantLastYearStockOptions: requiredIfCondition(
    yup.number(),
    ['coapplicantIncomeSource', 'coapplicantReceiveStockUnit'],
    (incomeSource, receiveStockUnit) =>
      incomeSource === 'employee' && receiveStockUnit === true,
    'Please enter your stock units.',
  ),
  coapplicantIndustry: requiredIfCoapplicantIncomeSource(
    yup.string(),
    ['self_employed_company', 'self_employed_trader_or_contractor'],
    'Please select your industry.',
  ),
  coapplicantOperationStartMonth: requiredIfCoapplicantIncomeSource(
    yup.string(),
    ['self_employed_company', 'self_employed_trader_or_contractor'],
    'Please select operation start month.',
  ),
  coapplicantOperationStartYear: requiredIfCoapplicantIncomeSource(
    yup.string(),
    ['self_employed_company', 'self_employed_trader_or_contractor'],
    'Please select operation start year.',
  ),
  coapplicantThisYearPersonalNetIncomeAfterTaxes:
    requiredIfCoapplicantIncomeSource(
      yup.number(),
      ['self_employed_company', 'self_employed_trader_or_contractor'],
      'Please enter your personal net income after taxes.',
    ),
  coapplicantLastYearPersonalNetIncomeAfterTaxes:
    requiredIfCoapplicantIncomeSource(
      yup.number(),
      ['self_employed_company', 'self_employed_trader_or_contractor'],
      'Please enter your personal net income after taxes.',
    ),

  coapplicantOwnershipPercentage: requiredIfCoapplicantIncomeSource(
    yup.number(),
    ['self_employed_company'],
    'Please enter ownership percentage.',
  ),
  coapplicantThisYearCompanyNetIncomeAfterTaxes:
    requiredIfCoapplicantIncomeSource(
      yup.number(),
      ['self_employed_company'],
      'Please enter your company net income after taxes.',
    ),
  coapplicantLastYearCompanyNetIncomeAfterTaxes:
    requiredIfCoapplicantIncomeSource(
      yup.number(),
      ['self_employed_company'],
      'Please enter your company net income after taxes.',
    ),
  coapplicantVisaSubClass: requiredIfPR(
    yup.string(),
    'Visa sub class is required.',
  ),
  coapplicantVisaExpiryDate: requiredIfPR(
    yup.string(),
    'Visa expire date is required.',
  ),
  ...nonPropertyFields,
  ...coapplicantNonPropertyFields,
});

export const incomeDetailLabelMapper = {
  incomeSource: 'What’s your main source of income?',
  nonPropertyLoan: 'How many non-property loans do you have?',
  nonPropertyLoanType: 'What’s your loan type and other loan info?',
  creditCard: 'What’s your credit details ?',
  userMessage: 'Is there anything you’d want us to know?',
  whereDidYouHearAboutUs: 'Help us understand your journey',
  coapplicantNonPropertyLoan:
    'How many non-property loans does the co-applicant have?',
};
